import React from "react";
import { Box, Typography } from "@mui/material";
import { getS3Url } from "../../utils/imageUtils";
import {
  AssetKicker,
  AssetTitle,
  Card,
  CardLabel,
  ChannelAvatar,
  LINKEDIN_ASSET_HEIGHT_PX,
  LINKEDIN_ASSET_WIDTH_PX,
  LinkedInAssetCanvas,
  MethodologyFooter,
  MUTED,
  NAVY,
  RADIUS,
  SANS,
  CARD_BORDER,
} from "./linkedinAssetChrome";

export { LINKEDIN_ASSET_WIDTH_PX, LINKEDIN_ASSET_HEIGHT_PX };

export interface LinkedInLivestreamMinute {
  minute: number;
  viewers: number;
}

export interface LinkedInLivestreamCompareRow {
  id: string;
  title: string;
  programName: string | null;
  channelName: string;
  channelImageS3Key: string | null;
  channelImageUrl: string | null;
  channelColor: string | null;
  startedAt: string | null;
  avgViewers: number;
  peakViewers: number;
  viewsDay7: number | null;
  viewsAsOf?: string | null;
  chatMessages: number | null;
  uniqueChatUsers: number | null;
  minutes: LinkedInLivestreamMinute[];
}

function channelImageSrc(stream: LinkedInLivestreamCompareRow): string {
  if (stream.channelImageS3Key) return getS3Url(stream.channelImageS3Key);
  if (stream.channelImageUrl) return stream.channelImageUrl;
  return "";
}

function hexToHsl(hex: string): [number, number, number] {
  const value = Number.parseInt(hex.slice(1), 16);
  const r = ((value >> 16) & 255) / 255;
  const g = ((value >> 8) & 255) / 255;
  const b = (value & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  if (max === min) return [0, 0, lightness];
  const delta = max - min;
  const saturation =
    lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  const hueBase =
    max === r
      ? (g - b) / delta + (g < b ? 6 : 0)
      : max === g
        ? (b - r) / delta + 2
        : (r - g) / delta + 4;
  return [hueBase * 60, saturation, lightness];
}

function hslToHex(hue: number, saturation: number, lightness: number): string {
  const wrapped = ((hue % 360) + 360) % 360;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = chroma * (1 - Math.abs(((wrapped / 60) % 2) - 1));
  const m = lightness - chroma / 2;
  const [r, g, b] =
    wrapped < 60
      ? [chroma, x, 0]
      : wrapped < 120
        ? [x, chroma, 0]
        : wrapped < 180
          ? [0, chroma, x]
          : wrapped < 240
            ? [0, x, chroma]
            : wrapped < 300
              ? [x, 0, chroma]
              : [chroma, 0, x];
  const channel = (part: number) =>
    Math.round((part + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/** One display color per channel. Hue comes from the channel image; saturation and lightness are fixed. */
function channelSeriesColor(stream: LinkedInLivestreamCompareRow): string {
  const fromImage =
    stream.channelColor && /^#[0-9a-fA-F]{6}$/.test(stream.channelColor)
      ? stream.channelColor
      : null;
  if (!fromImage)
    return hslToHex(hueFromName(stream.channelName || stream.id), 0.92, 0.64);
  const [hue, saturation] = hexToHsl(fromImage);
  return hslToHex(hue, Math.max(saturation, 0.92), 0.64);
}

function hueFromName(name: string): number {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1)
    hash = (hash * 33 + name.charCodeAt(index)) >>> 0;
  return hash % 360;
}

function displayName(row: LinkedInLivestreamCompareRow): string {
  return row.programName?.trim() || row.title.trim() || "Livestream";
}

function formatCount(value: number): string {
  return Math.round(value).toLocaleString("es-CL");
}

function formatCompact(value: number): string {
  const rounded = Math.round(value);
  if (Math.abs(rounded) < 1000) return rounded.toLocaleString("es-CL");
  const compact = rounded / 1000;
  return `${compact.toFixed(1).replace(".", ",")}K`;
}

function LockMark({
  color = MUTED,
  size = 20,
}: {
  color?: string;
  size?: number;
}) {
  return (
    <Box
      component="svg"
      viewBox="0 0 16 16"
      aria-hidden
      fill="none"
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap="round"
      sx={{
        width: size,
        height: size,
        display: "block",
        flexShrink: 0,
      }}
    >
      <rect x="3.2" y="7" width="9.6" height="6.4" rx="1.4" />
      <path d="M5.4 7V5.1a2.6 2.6 0 0 1 5.2 0V7" />
    </Box>
  );
}

const METRICS_FIT_ROWS = 4;
const METRICS_AVATAR_PX = 44;
const METRICS_NAME_PX = 22;
const METRICS_CHANNEL_PX = 14;
const METRICS_ROW_PAD_Y = 14;

function metricsRowScale(rowCount: number): number {
  return Math.min(1, METRICS_FIT_ROWS / Math.max(rowCount, 1));
}

const LockedChatMetric: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <Box
    aria-hidden
    sx={{
      alignSelf: "center",
      justifySelf: "end",
      display: "flex",
    }}
  >
    <LockMark size={size} />
  </Box>
);

function formatHhMm(date: Date): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Santiago",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const hour = (
    parts.find((part) => part.type === "hour")?.value ?? "00"
  ).padStart(2, "0");
  const minute = (
    parts.find((part) => part.type === "minute")?.value ?? "00"
  ).padStart(2, "0");
  return `${hour}:${minute}`;
}

function santiagoDateParts(
  iso: string | null,
): { weekday: string; day: string; month: string } | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("es-CL", {
    timeZone: "America/Santiago",
    weekday: "long",
    day: "numeric",
    month: "short",
  }).formatToParts(date);
  const weekday = parts.find((part) => part.type === "weekday")?.value ?? "";
  const day = parts.find((part) => part.type === "day")?.value ?? "";
  const month = (
    parts.find((part) => part.type === "month")?.value ?? ""
  ).replace(".", "");
  if (!weekday || !day || !month) return null;
  return { weekday, day, month };
}

function formatBroadcastDay(iso: string | null): string {
  const parts = santiagoDateParts(iso);
  if (!parts) return "";
  const label = `${parts.weekday.charAt(0).toUpperCase()}${parts.weekday.slice(1)}`;
  return [label, parts.day, parts.month].join(" ");
}

function earliestStart(streams: LinkedInLivestreamCompareRow[]): string | null {
  return (
    streams
      .map((stream) => stream.startedAt)
      .filter((value): value is string => Boolean(value))
      .sort()[0] ?? null
  );
}

function headlineQuestion(streams: LinkedInLivestreamCompareRow[]): string {
  const weekday = santiagoDateParts(earliestStart(streams))?.weekday;
  if (!weekday) return "¿Quién ganó la noche?";
  return `¿Quién ganó la noche del ${weekday}?`;
}

function niceCeil(value: number): number {
  if (value <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(value)));
  const fraction = value / exp;
  const ceil =
    fraction <= 1
      ? 1
      : fraction <= 1.5
        ? 1.5
        : fraction <= 2
          ? 2
          : fraction <= 2.5
            ? 2.5
            : fraction <= 5
              ? 5
              : 10;
  return ceil * exp;
}

function contiguousSegments(
  points: LinkedInLivestreamMinute[],
): LinkedInLivestreamMinute[][] {
  const segments: LinkedInLivestreamMinute[][] = [];
  let current: LinkedInLivestreamMinute[] = [];
  let previousMinute = -1;
  for (const point of points) {
    if (
      previousMinute >= 0 &&
      point.minute - previousMinute > 2 &&
      current.length
    ) {
      segments.push(current);
      current = [];
    }
    current.push(point);
    previousMinute = point.minute;
  }
  if (current.length) segments.push(current);
  return segments;
}

function smoothedPoints(
  points: LinkedInLivestreamMinute[],
): LinkedInLivestreamMinute[] {
  const radius = 8;
  const averaged = points.map((point, index) => {
    let weightSum = 0;
    let viewerSum = 0;
    for (let offset = -radius; offset <= radius; offset += 1) {
      const neighbor = points[index + offset];
      if (!neighbor) continue;
      const weight = radius + 1 - Math.abs(offset);
      viewerSum += neighbor.viewers * weight;
      weightSum += weight;
    }
    return {
      minute: point.minute,
      viewers: weightSum > 0 ? viewerSum / weightSum : point.viewers,
    };
  });

  const step = 5;
  return averaged.filter((point, index) => {
    return (
      index === 0 || index === averaged.length - 1 || point.minute % step === 0
    );
  });
}

function smoothPath(
  points: LinkedInLivestreamMinute[],
  xFor: (minute: number) => number,
  yFor: (viewers: number) => number,
): string {
  return contiguousSegments(points)
    .map((raw) => {
      const segment = smoothedPoints(raw).map((point) => ({
        x: xFor(point.minute),
        y: yFor(point.viewers),
      }));
      if (segment.length === 0) return "";
      if (segment.length === 1) {
        const only = segment[0]!;
        return `M${only.x.toFixed(1)} ${only.y.toFixed(1)}`;
      }
      let path = `M${segment[0]!.x.toFixed(1)} ${segment[0]!.y.toFixed(1)}`;
      for (let index = 0; index < segment.length - 1; index += 1) {
        const prev = segment[index - 1] ?? segment[index]!;
        const start = segment[index]!;
        const end = segment[index + 1]!;
        const next = segment[index + 2] ?? end;
        const cp1x = start.x + (end.x - prev.x) / 6;
        const cp1y = start.y + (end.y - prev.y) / 6;
        const cp2x = end.x - (next.x - start.x) / 6;
        const cp2y = end.y - (next.y - start.y) / 6;
        path += ` C${cp1x.toFixed(1)} ${cp1y.toFixed(1)} ${cp2x.toFixed(1)} ${cp2y.toFixed(1)} ${end.x.toFixed(1)} ${end.y.toFixed(1)}`;
      }
      return path;
    })
    .join(" ");
}

const CompareChart: React.FC<{
  streams: LinkedInLivestreamCompareRow[];
  colors: string[];
}> = ({ streams, colors }) => {
  const frameRef = React.useRef<HTMLDivElement>(null);
  const [frameWidth, setFrameWidth] = React.useState(0);
  React.useEffect(() => {
    const node = frameRef.current;
    if (!node) return;
    const measure = () => setFrameWidth(node.clientWidth || 0);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const width = 928;
  const height = 420;
  const pad = { l: 56, r: 22, t: 12, b: 36 };
  const earliestStart = streams
    .map((stream) => (stream.startedAt ? new Date(stream.startedAt) : null))
    .filter(
      (date): date is Date => date != null && !Number.isNaN(date.getTime()),
    )
    .sort((a, b) => a.getTime() - b.getTime())[0];
  const originMs = earliestStart?.getTime() ?? 0;
  const shiftMinutes = (stream: LinkedInLivestreamCompareRow) => {
    if (!originMs || !stream.startedAt) return 0;
    const start = new Date(stream.startedAt).getTime();
    if (Number.isNaN(start)) return 0;
    return (start - originMs) / 60_000;
  };
  const maxMinute = Math.max(
    1,
    ...streams.flatMap((stream) =>
      stream.minutes.map((point) => point.minute + shiftMinutes(stream)),
    ),
  );
  const maxViewers = Math.max(
    1,
    ...streams.flatMap((stream) =>
      stream.minutes.map((point) => point.viewers),
    ),
  );
  const yMax = niceCeil(maxViewers);
  const plotW = width - pad.l - pad.r;
  const plotH = height - pad.t - pad.b;
  const xFor = (minute: number) => pad.l + (minute / maxMinute) * plotW;
  const yFor = (viewers: number) => pad.t + (1 - viewers / yMax) * plotH;
  const yTicks = [0, yMax / 2, yMax];
  const axisClock = (minute: number) =>
    earliestStart
      ? formatHhMm(new Date(earliestStart.getTime() + minute * 60_000))
      : formatHhMm(new Date(minute * 60_000));
  const startMinute = earliestStart
    ? Number(
        new Intl.DateTimeFormat("en-GB", {
          timeZone: "America/Santiago",
          minute: "2-digit",
          hourCycle: "h23",
        })
          .formatToParts(earliestStart)
          .find((part) => part.type === "minute")?.value ?? "0",
      )
    : 0;
  const firstTick =
    startMinute === 0 || startMinute === 30
      ? 0
      : startMinute < 30
        ? 30 - startMinute
        : 60 - startMinute;
  const xTicks: number[] = [];
  for (let minute = firstTick; minute <= maxMinute; minute += 30)
    xTicks.push(minute);

  const logo = 42;
  const scale = frameWidth > 0 ? frameWidth / width : 1;
  const logoPx = logo * scale;
  const markers = streams.map((stream, index) => {
    const points = contiguousSegments(stream.minutes).flatMap((segment) =>
      smoothedPoints(segment),
    );
    const src = channelImageSrc(stream);
    if (!points.length || !src) return null;
    const peak = points.reduce((best, point) =>
      point.viewers > best.viewers ? point : best,
    );
    return {
      id: stream.id,
      src,
      color: colors[index]!,
      x: xFor(peak.minute + shiftMinutes(stream)),
      y: yFor(peak.viewers),
    };
  });
  for (let pass = 0; pass < 4; pass += 1) {
    for (let i = 0; i < markers.length; i += 1) {
      const marker = markers[i];
      if (!marker) continue;
      for (let j = 0; j < i; j += 1) {
        const other = markers[j];
        if (!other) continue;
        const dx = marker.x - other.x;
        const dy = marker.y - other.y;
        if (dx * dx + dy * dy < logo * logo) marker.x += logo * 0.85;
      }
      marker.x = Math.min(
        width - logo / 2 - 4,
        Math.max(pad.l + logo / 2, marker.x),
      );
      marker.y = Math.min(
        height - pad.b - logo / 2,
        Math.max(logo / 2 + 2, marker.y),
      );
    }
  }

  return (
    <Box ref={frameRef} sx={{ position: "relative", overflow: "visible" }}>
      <svg
        width="100%"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Espectadores minuto a minuto"
        style={{ display: "block", overflow: "visible" }}
      >
        {yTicks.map((tick) => {
          const y = yFor(tick);
          return (
            <g key={tick}>
              <line
                x1={pad.l}
                x2={width - pad.r}
                y1={y}
                y2={y}
                stroke={CARD_BORDER}
                strokeWidth={1}
              />
              <text
                x={pad.l - 10 + (tick === yMax ? 4 : 0)}
                y={y + 4}
                textAnchor="end"
                fill={MUTED}
                fontFamily="DM Sans, sans-serif"
                fontSize={14}
                fontWeight={600}
              >
                {formatCount(tick)}
              </text>
            </g>
          );
        })}
        {xTicks.map((minute) => (
          <text
            key={minute}
            x={xFor(minute)}
            y={height - 8}
            textAnchor="middle"
            fill={MUTED}
            fontFamily="DM Sans, sans-serif"
            fontSize={14}
            fontWeight={600}
          >
            {axisClock(minute)}
          </text>
        ))}
        {streams.map((stream, index) => (
          <path
            key={stream.id}
            d={smoothPath(
              stream.minutes,
              (minute) => xFor(minute + shiftMinutes(stream)),
              yFor,
            )}
            fill="none"
            stroke={colors[index]}
            strokeWidth={4.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
      </svg>
      {markers.map((marker) =>
        marker ? (
          <Box
            key={marker.id}
            component="img"
            src={marker.src}
            alt=""
            sx={{
              position: "absolute",
              width: logoPx,
              height: logoPx,
              left: marker.x * scale - logoPx / 2,
              top: marker.y * scale - logoPx / 2,
              borderRadius: RADIUS,
              objectFit: "cover",
              overflow: "hidden",
              bgcolor: "#fff",
              border: `2px solid ${marker.color}`,
              boxShadow: "0 1px 2px rgba(26, 25, 48, 0.12)",
              boxSizing: "border-box",
            }}
          />
        ) : null,
      )}
    </Box>
  );
};

const LinkedInLivestreamCompareAsset: React.FC<{
  streams: LinkedInLivestreamCompareRow[];
  showChatViewers?: boolean;
}> = ({ streams, showChatViewers = false }) => {
  const ranked = [...streams].sort(
    (a, b) => b.avgViewers - a.avgViewers || b.peakViewers - a.peakViewers,
  );
  const colors = ranked.map(channelSeriesColor);
  const broadcastDay = formatBroadcastDay(earliestStart(ranked));
  const question = headlineQuestion(ranked);
  const rowScale = metricsRowScale(ranked.length);
  const avatarPx = Math.round(METRICS_AVATAR_PX * rowScale);
  const namePx = Math.round(METRICS_NAME_PX * rowScale);
  const channelPx = Math.round(METRICS_CHANNEL_PX * rowScale);
  const rowPadY = Math.round(METRICS_ROW_PAD_Y * rowScale);
  const rowColumns = `${avatarPx}px minmax(0, 1.8fr) repeat(4, minmax(96px, 0.7fr))`;

  return (
    <LinkedInAssetCanvas>
      <Box>
        <AssetKicker>
          {[broadcastDay, "YouTube en vivo"].filter(Boolean).join(" · ")}
        </AssetKicker>
        <AssetTitle>
          Canales de TV en YouTube
          <br />
          {question}
        </AssetTitle>
      </Box>

      <Card sx={{ pt: "24px", pb: "12px" }}>
        <CardLabel>Espectadores en vivo</CardLabel>
        <CompareChart streams={ranked} colors={colors} />
      </Card>

      <Box>
        <Card>
          <CardLabel>Métricas consolidadas</CardLabel>
          <Box
            data-capture-pin=""
            sx={{
              display: "grid",
              gridTemplateColumns: rowColumns,
              columnGap: "12px",
              alignItems: "end",
              pb: "10px",
              borderBottom: `1px solid ${CARD_BORDER}`,
            }}
          >
            {[
              ["Programa"],
              ["Promedio", "Espectadores"],
              ["Peak", "Espectadores"],
              ["Mensajes", "en Chat"],
              ["Espectadores", "en Chat"],
            ].map((lines, index) => (
              <Typography
                key={lines.join(" ")}
                sx={{
                  gridColumn: index === 0 ? "1 / 3" : index + 2,
                  m: 0,
                  fontFamily: SANS,
                  fontWeight: lines[0] === "Promedio" ? 700 : 600,
                  fontSize: 14,
                  lineHeight: 1.15,
                  color: lines[0] === "Promedio" ? NAVY : MUTED,
                  textAlign: index === 0 ? "left" : "right",
                }}
              >
                {lines[0] === "Promedio" ? "↓ " : null}
                {lines[0]}
                {lines[1] ? (
                  <>
                    <br />
                    {lines[1]}
                  </>
                ) : null}
              </Typography>
            ))}
          </Box>
          {ranked.map((stream, index) => (
            <Box
              key={stream.id}
              data-capture-pin=""
              sx={{
                display: "grid",
                gridTemplateColumns: rowColumns,
                columnGap: "12px",
                rowGap: `${Math.max(1, Math.round(2 * rowScale))}px`,
                alignItems: "center",
                py: `${rowPadY}px`,
                borderBottom:
                  index === ranked.length - 1
                    ? "none"
                    : `1px solid ${CARD_BORDER}`,
              }}
            >
              <ChannelAvatar
                src={channelImageSrc(stream)}
                accentColor={colors[index]!}
                size={avatarPx}
              />
              <Box sx={{ minWidth: 0, alignSelf: "center" }}>
                <Typography
                  sx={{
                    m: 0,
                    fontFamily: SANS,
                    fontWeight: 600,
                    fontSize: namePx,
                    lineHeight: 1.1,
                    color: NAVY,
                  }}
                >
                  {displayName(stream)}
                </Typography>
                <Typography
                  sx={{
                    m: "2px 0 0",
                    fontFamily: SANS,
                    fontWeight: 500,
                    fontSize: channelPx,
                    lineHeight: 1.1,
                    color: MUTED,
                  }}
                >
                  {stream.channelName}
                </Typography>
              </Box>
              <Typography
                sx={{
                  alignSelf: "center",
                  m: 0,
                  fontFamily: SANS,
                  fontWeight: 700,
                  fontSize: namePx,
                  lineHeight: 1,
                  color: NAVY,
                  textAlign: "right",
                }}
              >
                {formatCompact(stream.avgViewers)}
              </Typography>
              <Typography
                sx={{
                  alignSelf: "center",
                  m: 0,
                  fontFamily: SANS,
                  fontWeight: 600,
                  fontSize: namePx,
                  lineHeight: 1,
                  color: NAVY,
                  textAlign: "right",
                }}
              >
                {formatCompact(stream.peakViewers)}
              </Typography>
              <Typography
                sx={{
                  alignSelf: "center",
                  m: 0,
                  fontFamily: SANS,
                  fontWeight: 600,
                  fontSize: namePx,
                  lineHeight: 1,
                  color: NAVY,
                  textAlign: "right",
                }}
              >
                {stream.chatMessages == null
                  ? "—"
                  : formatCompact(stream.chatMessages)}
              </Typography>
              {showChatViewers ? (
                <Typography
                  sx={{
                    alignSelf: "center",
                    m: 0,
                    fontFamily: SANS,
                    fontWeight: 600,
                    fontSize: namePx,
                    lineHeight: 1,
                    color: NAVY,
                    textAlign: "right",
                  }}
                >
                  {stream.uniqueChatUsers == null
                    ? "—"
                    : formatCompact(stream.uniqueChatUsers)}
                </Typography>
              ) : (
                <LockedChatMetric size={Math.round(20 * rowScale)} />
              )}
            </Box>
          ))}
        </Card>
        {showChatViewers ? null : (
          <Box
            data-capture-pin=""
            sx={{
              mt: "8px",
              pr: "28px",
              py: 0,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "8px",
              fontFamily: SANS,
              fontWeight: 600,
              fontSize: 16,
              lineHeight: 1.2,
              color: MUTED,
            }}
          >
            <LockMark color={MUTED} size={16} />
            <Box component="span">Espectadores en chat disponibles en Podscope</Box>
          </Box>
        )}
      </Box>

      <MethodologyFooter>
        {`: espectadores cada minuto por hora real de cada transmisión. Promedio espectadores: media de espectadores en vivo por minuto. Peak espectadores: máximo de espectadores en vivo. Espectadores en chat: personas distintas interactuando en el chat en vivo. Mensajes en chat: total de mensajes en chat en vivo.`}
      </MethodologyFooter>
    </LinkedInAssetCanvas>
  );
};

export default LinkedInLivestreamCompareAsset;
